<template>
  <LargeModal
    :show="show"
    title="Request guide"
    subtitle="Use the search below to find guides and request their services for this engagement"
    @close="close"
  >
    <template v-if="!guide">
      <div class="bg-gray-100 overflow-visible bg-white shadow overflow-hidden sm:rounded-md text-font mb-5">
        <div
          class="flex justify-between p-4 cursor-pointer"
          @click="toggleSearch"
        >
          <p>Filters</p>

          <svg
            v-if="show_search"
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M5 15l7-7 7 7"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
        <div
          v-if="show_search"
        >
          <div>
            <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200">
              <label class="block text-sm font-medium text-font sm:mt-px"> Destination </label>
              <div class="flex items-center mt-1 sm:mt-0 sm:col-span-2">
                <img
                  :src="engagement.place.image"
                  :alt="engagement.place.name"
                  class="flex-shrink-0 rounded-sm h-4 w-6 mr-2"
                >
                <p>{{ engagement.place.name }} <small>{{ engagement.place.area }}</small></p>
              </div>
            </div>
            <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label class="block text-sm font-medium text-font mt-2"> Show tour guides </label>
              <div class="mt-1 sm:mt-0 sm:col-span-2">
                <Switch
                  v-model="show_tour_guides"
                  :class="[show_tour_guides ? 'bg-indigo-600' : 'bg-gray-200', 'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500']"
                >
                  <span class="sr-only">Show tour guides</span>
                  <span :class="[show_tour_guides ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200']">
                    <span
                      :class="[show_tour_guides ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity']"
                      aria-hidden="true"
                    >
                      <svg
                        class="h-3 w-3 text-gray-400"
                        fill="none"
                        viewBox="0 0 12 12"
                      >
                        <path
                          d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                    <span
                      :class="[show_tour_guides ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity']"
                      aria-hidden="true"
                    >
                      <svg
                        class="h-3 w-3 text-indigo-600"
                        fill="currentColor"
                        viewBox="0 0 12 12"
                      >
                        <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                      </svg>
                    </span>
                  </span>
                </Switch>
              </div>
            </div>
            <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label class="block text-sm font-medium text-font mt-2"> Show tour leaders </label>
              <div class="mt-1 sm:mt-0 sm:col-span-2">
                <Switch
                  v-model="show_tour_leaders"
                  :class="[show_tour_leaders ? 'bg-indigo-600' : 'bg-gray-200', 'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500']"
                >
                  <span class="sr-only">Show tour leaders</span>
                  <span :class="[show_tour_leaders ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200']">
                    <span
                      :class="[show_tour_leaders ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity']"
                      aria-hidden="true"
                    >
                      <svg
                        class="h-3 w-3 text-gray-400"
                        fill="none"
                        viewBox="0 0 12 12"
                      >
                        <path
                          d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                    <span
                      :class="[show_tour_leaders ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity']"
                      aria-hidden="true"
                    >
                      <svg
                        class="h-3 w-3 text-indigo-600"
                        fill="currentColor"
                        viewBox="0 0 12 12"
                      >
                        <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                      </svg>
                    </span>
                  </span>
                </Switch>
              </div>
            </div>
            <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label class="block text-sm font-medium text-font mt-2"> Languages </label>
              <div class="mt-1 sm:mt-0 sm:col-span-2">
                <LanguageSelectPickerMultiple
                  v-model="languages"
                  label=""
                />
              </div>
            </div>
            <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label class="block text-sm font-medium text-font mt-2"> Age range </label>
              <div class="mt-1 sm:mt-0 sm:col-span-2">
                <select
                  v-model="age_range"
                  class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">
                    Any age
                  </option>
                  <option value="0">
                    30 and younger
                  </option>
                  <option value="1">
                    31 - 40
                  </option>
                  <option value="2">
                    41 - 50
                  </option>
                  <option value="3">
                    51 and older
                  </option>
                </select>
              </div>
            </div>
            <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label class="block text-sm font-medium text-font mt-2"> Gender </label>
              <div class="mt-1 sm:mt-0 sm:col-span-2">
                <select
                  v-model="gender"
                  class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">
                    Any gender
                  </option>
                  <option
                    v-for="(value, id) in genders"
                    :value="id"
                  >
                    {{ value }}
                  </option>
                </select>
              </div>
            </div>
            <div
              v-if="false"
              class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start"
            >
              <label class="block text-sm font-medium text-font mt-2"> Accepts payment form </label>
              <div class="grid grid-cols-3 col-span-2">
                <div
                  v-for="(value,id) in payment_options"
                  class="relative flex items-start"
                >
                  <div class="flex items-center h-5">
                    <input
                      :id="id"
                      v-model="payment_options_selected[id]"
                      :name="id"
                      type="checkbox"
                      class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    >
                  </div>
                  <div class="ml-3 text-sm">
                    <label
                      :for="id"
                      class="font-text"
                    >{{ value }}</label>
                  </div>
                </div>
              </div>
            </div>

            <div class="p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
              <label class="block text-sm font-medium text-font sm:mt-px"> Search </label>
              <div class="flex items-center mt-1 sm:mt-0 sm:col-span-2">
                <input
                  v-model="search_string"
                  class="p-2 block max-w-lg w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                  placeholder="Search for name, company or text in the profile..."
                >
              </div>
            </div>
            <div class="flex justify-end p-4">
              <CreateButton @click="filter">
                Filter results
              </CreateButton>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="flex justify-center"
      >
        <three-dot-fade size="40" />
      </div>
      <div
        v-else
        class="bg-white shadow overflow-hidden sm:rounded-md"
      >
        <ul
          role="list"
          class="divide-y divide-gray-200"
        >
          <li
            v-for="guide_list in results.data"
            :key="guide_list.id"
          >
            <div class="flex items-center px-4 py-4 sm:px-6">
              <div class="min-w-0 flex-1 flex items-center">
                <div class="flex-shrink-0">
                  <img
                    class="h-12 w-12 rounded-full"
                    :src="guide_list.user.profile_photo_url"
                    :alt="guide_list.user.name"
                  >
                </div>
                <div class="min-w-0 flex-1 px-4">
                  <div>
                    <p class="text-sm font-medium text-indigo-600 truncate">
                      {{ guide_list.user.name }} <span class="text-font text-xs">{{ guide_list.age }} yo</span>
                    </p>
                    <p class="flex items-center text-sm text-gray-500">
                      <span
                        v-if="guide_list.available_as_guide"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mr-2"
                      > Tour Guide </span>
                      <span
                        v-if="guide_list.available_as_leader"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      > Tour Leader </span>
                    </p>
                    <p
                      v-if="!guide_list.team.personal_team"
                      class="flex items-center text-sm text-gray-500"
                    >
                      <span class="truncate">{{ guide_list.team.name }}</span>
                    </p>
                  </div>
                </div>
                <div class="flex flex-col min-w-0 flex-1 px-4 text-sm text-font">
                  <span class="flex items-center">
                    Languages:
                    <img
                      v-for="language in guide_list.languages"
                      :src="language.image"
                      :alt="language.name"
                      class="flex-shrink-0 rounded-sm h-3 w-4 ml-2"
                    >
                  </span>
                  <p>Tour Types: <span class="italic">{{ guide_list.tour_types }}</span></p>
                </div>
                <span
                  v-if="requested(guide_list)"
                  class="bg-gray-100 text-font-light rounded-md py-2 px-2 border border-font-light"
                >Requested</span>
                <CreateButton
                  v-else
                  @click="view(guide_list)"
                >
                  View
                </CreateButton>
              </div>
            </div>
          </li>
        </ul>
        <nav
          class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6"
          aria-label="Pagination"
        >
          <div class="hidden sm:block">
            <p class="text-sm text-gray-700">
              Showing
              {{ ' ' }}
              <span class="font-medium">{{ results.from }}</span>
              {{ ' ' }}
              to
              {{ ' ' }}
              <span class="font-medium">{{ results.to }}</span>
              {{ ' ' }}
              of
              {{ ' ' }}
              <span class="font-medium">{{ results.total }}</span>
              {{ ' ' }}
              results
            </p>
          </div>
          <div class="flex-1 flex justify-between sm:justify-end">
            <p
              v-if="results.current_page > 1"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
              @click="previous"
            >
              Previous
            </p>
            <p
              v-else
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-400 bg-white hover:bg-gray-50"
            >
              Previous
            </p>
            <p
              v-if="results.current_page < results.last_page"
              class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
              @click="next"
            >
              Next
            </p>
            <p
              v-else
              class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-400 bg-white hover:bg-gray-50"
            >
              Next
            </p>
          </div>
        </nav>
      </div>
    </template>
    <template v-else>
      <article>
        <!-- Profile header -->
        <span
          class="pb-4 px-4 sm:px-6 lg:px-8 flex cursor-pointer items-center text-sm text-font hover:text-font-darkest"
          @click="guide = null"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to search
        </span>
        <div>
          <div class="max-w-5xl flex justify-between px-4 sm:px-6 lg:px-8">
            <div class="flex sm:items-end sm:space-x-5">
              <div class="flex items-center space-x-10">
                <img
                  class="h-12 w-12 rounded-full ring-4 ring-white"
                  :src="guide.user.profile_photo_url"
                  alt=""
                >

                <div class="flex flex-col">
                  <h1 class="text-2xl font-bold text-gray-900 truncate">
                    {{ guide.user.name }}
                  </h1>
                  <div class="flex">
                    <span
                      v-if="guide.available_as_guide"
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mr-2"
                    > Tour Guide </span>
                    <span
                      v-if="guide.available_as_leader"
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                    > Tour Leader </span>
                  </div>
                </div>
              </div>
            </div>
              <div class="flex justify-center items-center text-xs">
                  <span v-if="guide.review_count > 0" class="flex flex-col justify-center items-center">
                      <star-rating
                        :rating="guide.rating"
                        :star-size="14"
                        :read-only="true"
                        :show-rating="false"
                        :round-start-rating="false"
                      />
                      <span>Rated {{ Math.round(guide.rating * 100) / 100 }} in {{ guide.review_count }} reviews</span>
                  </span>
                  <span v-else>
                      Not reviews yet
                  </span>
              </div>
          </div>
        </div>

        <!-- Tabs -->
        <div
          v-if="false"
          class="mt-6 sm:mt-2 2xl:mt-5"
        >
          <div class="border-b border-gray-200">
            <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <nav
                class="-mb-px flex space-x-8"
                aria-label="Tabs"
              >
                <a
                  v-for="tab in [
                    { name: 'Profile', href: '#', current: true },
                    { name: 'Calendar', href: '#', current: false },
                    { name: 'Recognition', href: '#', current: false },
                  ]"
                  :key="tab.name"
                  :href="tab.href"
                  :class="[tab.current ? 'border-pink-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm']"
                  :aria-current="tab.current ? 'page' : undefined"
                >
                  {{ tab.name }}
                </a>
              </nav>
            </div>
          </div>
        </div>

        <!-- Description list -->
        <div class="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div class="sm:col-span-2">
              <dt class="text-sm font-medium text-gray-500">
                About
              </dt>
              <dd class="mt-1 text-sm text-gray-900">
                {{ guide.about }}
              </dd>
            </div>
            <div class="sm:col-span-1">
              <dt class="text-sm font-medium text-gray-500">
                Name
              </dt>
              <dd class="mt-1 text-sm text-gray-900">
                {{ guide.user.name }}
              </dd>
            </div>
            <div class="sm:col-span-1">
              <dt class="text-sm font-medium text-gray-500">
                Age
              </dt>
              <dd class="mt-1 text-sm text-gray-900">
                {{ guide.age }} (<FormattedDate :date="guide.dob" />)
              </dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-sm font-medium text-gray-500">
                Languages
              </dt>
              <dd class="flex space-x-4 mt-1 max-w-prose text-sm text-gray-900">
                <div
                  v-for="language in guide.languages"
                  class="flex items-center"
                >
                  <div>
                    <img
                      class="inline-block w-6 rounded-sm"
                      :src="language.image"
                      :alt="language.name"
                    >
                  </div>
                  <div class="ml-2">
                    <p class="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {{ language.name }}
                    </p>
                    <p class="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                      {{ language.native }}
                    </p>
                  </div>
                </div>
              </dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-sm font-medium text-gray-500">
                Places
              </dt>
              <dd class="flex space-x-4 mt-1 max-w-prose text-sm text-gray-900">
                <div
                  v-for="place in guide.places"
                  class="flex items-center"
                >
                  <div>
                    <img
                      class="inline-block w-6 rounded-sm"
                      :src="place.image"
                      :alt="place.name"
                    >
                  </div>
                  <div class="ml-2">
                    <p class="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {{ place.name }}
                    </p>
                    <p class="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                      {{ place.area }}
                    </p>
                  </div>
                </div>
              </dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-sm font-medium text-gray-500">
                Tour Types
              </dt>
              <dd
                class="mt-1 max-w-prose text-sm text-gray-900 space-y-5"
                v-html="guide.tour_types"
              />
            </div>
            <div class="sm:col-span-2">
              <dt class="text-sm font-medium text-gray-500">
                Accepted Payment Methods
              </dt>
              <dd
                class="mt-1 max-w-prose text-sm text-gray-900 space-y-5"
              >
                {{ active_payment_options.join(', ') }}
              </dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-sm font-medium text-gray-500">
                Experience
              </dt>
              <dd
                class="mt-1 max-w-prose text-sm text-gray-900 space-y-5"
                v-html="guide.experience"
              />
            </div>
            <div class="sm:col-span-2">
              <dt class="text-sm font-medium text-gray-500">
                Knowledge
              </dt>
              <dd
                class="mt-1 max-w-prose text-sm text-gray-900 space-y-5"
                v-html="guide.knowledge"
              />
            </div>
          </dl>
        </div>
      </article>
    </template>
    <template #footer>
      <div
        v-if="guide"
        class="flex w-full justify-end items-center"
      >
        <span class="mr-3">Respond by:</span>
        <Datepicker
          v-model="respond_by"
        />
        <CreateButton
          class="mx-8"
          @click="request(guide)"
        >
          Request
        </CreateButton>
      </div>
      <CreateButton
        v-else
        class="mx-8"
        @click="close"
      >
        Done
      </CreateButton>
    </template>
  </LargeModal>
</template>

<script setup>

import {
  watch, ref, computed, onMounted,
} from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import LargeModal from '@/ComponentsDeprecated/Modal/LargeModal';
import CreateButton from '@/ComponentsDeprecated/Buttons/CreateButton';
import ThreeDotFade from '@/ComponentsDeprecated/Loaders/ThreeDotFade';
import LanguageSelectPickerMultiple from '@/ComponentsDeprecated/Select/LanguageSelectPickerMultiple';
import '@vuepic/vue-datepicker/dist/main.css';
import FormattedDate from '@/ComponentsDeprecated/Time/FormattedDate';
import StarRating from "vue-star-rating";
import { router as Inertia } from '@inertiajs/core';

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
    default: false,
  },
  engagement: {
    type: Object,
    required: true,
  },
  genders: {
    type: Object,
    required: true,
  },

  payment_options: {
    type: Object,
    required: true,
  },

});

const results = ref({
  data: [],
});
const loading = ref(false);
const payment_options_selected = ref({});
const requesting = ref(false);
const page = ref(1);
const show_search = ref(false);
const search_string = ref('');
const per_page = 5;
const languages = ref([]);
const guide = ref(null);
const respond_by = ref(null);
const engagement_request = ref(null);
const show_tour_guides = ref(true);
const show_tour_leaders = ref(true);
const age_range = ref('');
const gender = ref('');
const active_payment_options = computed(() => {
  const values = [];
  if (guide.value === null) {
    return values;
  }
  for (const idx in guide.value.payment_options) {
    values.push(props.payment_options[guide.value.payment_options[idx]]);
  }

  return values;
});

onMounted(() => {
  Object.keys(props.payment_options).forEach((key) => {
    payment_options_selected.value[key] = true;
  });
  const currentDate = new Date();
  respond_by.value = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7, 0, 0, 0, 0);
});
const close = () => {
  emit('close');
};

const emit = defineEmits({ close: (_value) => true });

function toggleSearch() {
  show_search.value = !show_search.value;
}
function view(g) {
  guide.value = g;
}
function next() {
  page.value++;
  search();
}
function previous() {
  page.value--;
  search();
}
function filter() {
  show_search.value = false;
  page.value = 1;
  search();
}
function requested(g) {
  return props.engagement.requests.findIndex((x) => x.guide_profile_id === g.id) !== -1;
}
function search() {
  loading.value = true;
  axios
    .post(`${route('api.guides.search')}?page=${page.value}`, {
      per_page,
      destination: props.engagement.place.id,
      search_string: search_string.value,
      languages: languages.value,
      show_tour_leaders: show_tour_leaders.value,
      show_tour_guides: show_tour_guides.value,
      gender: gender.value,
      age_range: age_range.value,
      payment_options: payment_options_selected.value,
    })
    .then((response) => {
      results.value = response.data;
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}
function request(g) {
  requesting.value = true;
  Inertia
    .post(route('engagements.requests.store'), {
      engagement_id: props.engagement.id,
      guide_profile_id: g.id,
      respond_by: respond_by.value,
    }, {
      onSuccess: close()
    })
}

watch(() => props.show, (newValue) => {
  if (newValue && results.value.data.length === 0) {
    languages.value = [props.engagement.language];
    search();
  }
});
watch(() => engagement_request.value, (newValue) => {
  if (newValue.success) {
    emit('request', newValue.request);
  }
});
</script>
